<script setup lang="ts">
    import BetDialogEvent from '@/pages/core/betDialog/BetDialogEvent.vue';
    import BetDialogInfo from '@/pages/core/betDialog/BetDialogInfo.vue';
    import BetDialogConfirmationInfo from '@/pages/core/betDialog/BetDialogConfirmationInfo.vue';
    import { useUserStore } from '@/stores/user';
    import { useTheme } from 'vuetify';
    import { BetSelection, EventFeedType } from '@/constants';
    import { useAppStore } from '@/stores/app';

    const { id = '0' } = defineProps<{
        id: string;
    }>();

    const appStore = useAppStore();
    const bettingData = appStore.betFeedData[id];

    const emit = defineEmits(['confirmed']);

    const dialog = ref(false);
    const userStore = useUserStore();
    const theme = useTheme();
    const { t } = useI18n();

    function open() {
        dialog.value = true;
    }

    function close() {
        dialog.value = false;
    }

    defineExpose({ open, close });

    function cancel() {
        close();
    }

    function confirm() {
        emit('confirmed', close);
    }

    const scoreNotConfirmed = computed(() => {
        const data = appStore.betFeedData[id];
        const isCorrectSelection =
            data.betSelectionId !== BetSelection.CornersHandicapHome &&
            data.betSelectionId !== BetSelection.CornersHandicapAway &&
            data.betSelectionId !== BetSelection.CornersGoalOver &&
            data.betSelectionId !== BetSelection.CornersGoalUnder;

        const isLive = data.eventFeedType === EventFeedType.Live;
        const time = isLive ? data.time : null;
        const score = isLive ? data.scoreFull : null;

        return isCorrectSelection && isLive && (!score || !time);
    });

    const cornersNotConfirmed = computed(() => {
        const data = appStore.betFeedData[id];
        const isCorrectSelection =
            data.betSelectionId === BetSelection.CornersHandicapHome ||
            data.betSelectionId === BetSelection.CornersHandicapAway ||
            data.betSelectionId === BetSelection.CornersGoalOver ||
            data.betSelectionId === BetSelection.CornersGoalUnder;

        const isLive = data.eventFeedType === EventFeedType.Live;
        const corners = isLive ? data.corners : null;

        return isCorrectSelection && isLive && !corners;
    });
</script>

<template>
    <v-dialog v-model="dialog" width="420" persistent @keydown.esc="cancel" @keydown.enter="cancel" :scrim="true">
        <v-card class="bet-dialog">
            <v-toolbar density="compact" flat :color="theme.name.value === 'dark' ? '#1e1f22' : 'white'">
                <v-toolbar-title class="text-h5">
                    {{ t('betDialog.orderConfirmTitle') }}
                </v-toolbar-title>
            </v-toolbar>
            <v-card-text class="px-5 pt-2 font14">
                <v-container class="pa-3">
                    <v-row class="h-22 d-flex flex-column justify-center">
                        <BetDialogEvent :id="id" isConfirm></BetDialogEvent>
                        <v-spacer></v-spacer>
                    </v-row>
                    <v-row>
                        <BetDialogInfo :id="id" :odds="bettingData.odds" :stake="bettingData.stake" isConfirm></BetDialogInfo>
                    </v-row>
                    <v-row class="h-32 d-flex align-center pt-2">
                        <span class="pr-2">
                            {{ t('betDialog.stake') }}:
                            <span class="bold">{{ userStore.currency.symbol }}{{ bettingData.stake }}</span>
                        </span>

                        <span>
                            {{ t('betDialog.returns') }}:
                            <span class="">{{ userStore.currency.symbol }}{{ bettingData.returns }}</span>
                        </span>
                    </v-row>
                    <v-row class="h-32 d-flex flex-column align-start justify-center pt-1">
                        <span>{{ t('betDialogConfirm.confirmingText') }}:</span>
                    </v-row>
                    <v-row>
                        <BetDialogConfirmationInfo :id="id"></BetDialogConfirmationInfo>
                    </v-row>
                    <v-row v-if="scoreNotConfirmed" class="orangeText py-2">
                        <p>{{ t('betDialog.orderConfirmText') }}</p>
                    </v-row>
                    <v-row v-else-if="cornersNotConfirmed" class="orangeText py-2">
                        <p>{{ t('betDialog.orderCornersConfirmText') }}</p>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="pt-0">
                <v-spacer></v-spacer>
                <v-btn tag="a" density="default" color="grey" variant="text" @click="cancel()">
                    {{ t('action.cancel') }}
                </v-btn>
                <v-btn :disabled="!bettingData.isActive" density="default" color="tbGreen" elevation="0" class="text-white" tag="a" variant="flat" @click="confirm()">
                    {{ t('betDialogConfirm.confirm') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
    .v-theme--dark .v-card.bet-dialog {
        border: 1px solid #404148;
    }

    .v-theme--light .v-card.bet-dialog {
        border: thin solid rgba(0, 0, 0, 0.12);
    }

    .v-theme--dark :deep(.v-btn:not(.text-tbGreen) .v-btn__content) {
        color: #f0f0f0;
    }
</style>
